import React from 'react';
import { resetChunkReloadAttempts } from '@/lib/chunkReload';
import { posthog } from '@/lib/posthog';

interface State { hasError: boolean; error: Error | null }

/**
 * Catches React render errors (which bypass window.onerror) and forwards
 * them to PostHog. Without this, errors like the stale-bundle React #426
 * reach users without ever appearing in analytics.
 *
 * We intentionally keep the fallback UI simple and reload-friendly. The
 * session replay + the `posthog.captureException` call below give enough
 * context to diagnose without keeping the broken subtree mounted.
 */
class AppErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    try {
      posthog.captureException(error, {
        source: 'react_error_boundary',
        componentStack: info.componentStack,
        url: window.location.href,
      });
    } catch { /* noop — never let error reporting crash the fallback */ }
  }

  handleReload = () => {
    // The user explicitly asked to retry, so give the self-heal a fresh
    // attempt — the loop guard only exists to stop AUTOMATIC reloads.
    resetChunkReloadAttempts();
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f] p-6">
        <div className="max-w-md w-full bg-[#16161e] border border-white/8 rounded-xl p-6 text-center">
          <h1 className="text-lg font-semibold text-white mb-2">Something went wrong</h1>
          <p className="text-sm text-[#a0a0b0] mb-5">
            The error has been reported. Reloading usually fixes it — we ship
            updates frequently and your tab may be running an old copy.
          </p>
          <button
            onClick={this.handleReload}
            className="px-4 py-2 rounded-lg bg-iris-600 hover:bg-iris-500 text-[#0C0C12] text-sm font-medium"
          >Reload</button>
        </div>
      </div>
    );
  }
}

export default AppErrorBoundary;
